import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, adminHeaders, getAdminToken, setAdminToken, formatINR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { LogOut, Plus, Trash2, Pencil } from "lucide-react";

const LOGO_URL = "/logo.png";

const EMPTY_PKG = {
  title: "",
  destination: "",
  category: "Family",
  duration_days: 5,
  duration_nights: 4,
  price_inr: 20000,
  original_price_inr: 25000,
  short_description: "",
  description: "",
  hero_image: "",
  gallery: [],
  highlights: [],
  inclusions: [],
  exclusions: [],
  itinerary: [],
  featured: false,
  active: true,
};

const STATUSES = ["new", "contacted", "converted", "closed"];

const asLines = (arr) => (arr || []).join("\n");
const fromLines = (s) => (s || "").split("\n").map((x) => x.trim()).filter(Boolean);

const asItineraryText = (arr) =>
  (arr || [])
    .map((d) => `Day ${d.day} | ${d.title} | ${d.description}`)
    .join("\n");

const parseItinerary = (text) =>
  (text || "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line, i) => {
      const parts = line.split("|").map((p) => p.trim());
      const dayMatch = parts[0]?.match(/\d+/);
      return {
        day: dayMatch ? parseInt(dayMatch[0], 10) : i + 1,
        title: parts[1] || "Day plan",
        description: parts[2] || "",
      };
    });

function PackageForm({ initial, onSave, onCancel }) {
  const [form, setForm] = useState({
    ...initial,
    _highlights: asLines(initial.highlights),
    _inclusions: asLines(initial.inclusions),
    _exclusions: asLines(initial.exclusions),
    _gallery: asLines(initial.gallery),
    _itinerary: asItineraryText(initial.itinerary),
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = () => {
    const payload = {
      ...form,
      highlights: fromLines(form._highlights),
      inclusions: fromLines(form._inclusions),
      exclusions: fromLines(form._exclusions),
      gallery: fromLines(form._gallery),
      itinerary: parseItinerary(form._itinerary),
      duration_days: Number(form.duration_days),
      duration_nights: Number(form.duration_nights),
      price_inr: Number(form.price_inr),
      original_price_inr: form.original_price_inr
        ? Number(form.original_price_inr)
        : null,
    };
    delete payload._highlights;
    delete payload._inclusions;
    delete payload._exclusions;
    delete payload._gallery;
    delete payload._itinerary;
    onSave(payload);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="package-form">
      <div className="md:col-span-2">
        <Label>Title</Label>
        <Input data-testid="pkg-title" value={form.title} onChange={(e) => set("title", e.target.value)} />
      </div>
      <div>
        <Label>Destination</Label>
        <Input value={form.destination} onChange={(e) => set("destination", e.target.value)} />
      </div>
      <div>
        <Label>Category</Label>
        <Select value={form.category} onValueChange={(v) => set("category", v)}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            {["Family", "Honeymoon", "Adventure", "Group", "Luxury", "Budget"].map((c) => (
              <SelectItem key={c} value={c}>{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Days</Label>
        <Input type="number" value={form.duration_days} onChange={(e) => set("duration_days", e.target.value)} />
      </div>
      <div>
        <Label>Nights</Label>
        <Input type="number" value={form.duration_nights} onChange={(e) => set("duration_nights", e.target.value)} />
      </div>
      <div>
        <Label>Price (INR)</Label>
        <Input type="number" value={form.price_inr} onChange={(e) => set("price_inr", e.target.value)} />
      </div>
      <div>
        <Label>Original Price (INR)</Label>
        <Input type="number" value={form.original_price_inr || ""} onChange={(e) => set("original_price_inr", e.target.value)} />
      </div>
      <div className="md:col-span-2">
        <Label>Hero image URL</Label>
        <Input value={form.hero_image} onChange={(e) => set("hero_image", e.target.value)} />
      </div>
      <div className="md:col-span-2">
        <Label>Short description</Label>
        <Input value={form.short_description} onChange={(e) => set("short_description", e.target.value)} />
      </div>
      <div className="md:col-span-2">
        <Label>Description</Label>
        <Textarea rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} />
      </div>
      <div>
        <Label>Highlights (one per line)</Label>
        <Textarea rows={5} value={form._highlights} onChange={(e) => set("_highlights", e.target.value)} />
      </div>
      <div>
        <Label>Gallery URLs (one per line)</Label>
        <Textarea rows={5} value={form._gallery} onChange={(e) => set("_gallery", e.target.value)} />
      </div>
      <div>
        <Label>Inclusions (one per line)</Label>
        <Textarea rows={5} value={form._inclusions} onChange={(e) => set("_inclusions", e.target.value)} />
      </div>
      <div>
        <Label>Exclusions (one per line)</Label>
        <Textarea rows={5} value={form._exclusions} onChange={(e) => set("_exclusions", e.target.value)} />
      </div>
      <div className="md:col-span-2">
        <Label>Itinerary (one day per line, format: <code>Day N | Title | Description</code>)</Label>
        <Textarea rows={6} value={form._itinerary} onChange={(e) => set("_itinerary", e.target.value)} />
      </div>
      <div className="flex items-center gap-4 md:col-span-2">
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={!!form.featured} onChange={(e) => set("featured", e.target.checked)} />
          Featured
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={!!form.active} onChange={(e) => set("active", e.target.checked)} />
          Active
        </label>
      </div>
      <div className="md:col-span-2 flex justify-end gap-2 pt-2">
        <Button variant="outline" onClick={onCancel} data-testid="pkg-cancel">Cancel</Button>
        <Button className="btn-primary" onClick={save} data-testid="pkg-save">Save</Button>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [packages, setPackages] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [editing, setEditing] = useState(null); // null | {pkg}
  const [creating, setCreating] = useState(false);

  const loadAll = useCallback(async () => {
    try {
      const [s, p, i] = await Promise.all([
        api.get("/admin/stats", adminHeaders()),
        api.get("/packages", { params: { active_only: false } }),
        api.get("/inquiries", adminHeaders()),
      ]);
      setStats(s.data);
      setPackages(p.data);
      setInquiries(i.data);
    } catch (e) {
      if (e.response?.status === 401) {
        toast.error("Session expired");
        nav("/admin/login");
      }
    }
  }, [nav]);

  useEffect(() => {
    if (!getAdminToken()) {
      nav("/admin/login");
      return;
    }
    loadAll();
  }, [loadAll, nav]);

  const logout = () => {
    setAdminToken(null);
    nav("/admin/login");
  };

  const createPkg = async (payload) => {
    try {
      await api.post("/packages", payload, adminHeaders());
      toast.success("Package created");
      setCreating(false);
      loadAll();
    } catch {
      toast.error("Could not create package");
    }
  };

  const updatePkg = async (payload) => {
    try {
      await api.put(`/packages/${editing.id}`, payload, adminHeaders());
      toast.success("Package updated");
      setEditing(null);
      loadAll();
    } catch {
      toast.error("Could not update package");
    }
  };

  const deletePkg = async (id) => {
    if (!window.confirm("Delete this package?")) return;
    try {
      await api.delete(`/packages/${id}`, adminHeaders());
      toast.success("Deleted");
      loadAll();
    } catch {
      toast.error("Could not delete");
    }
  };

  const updateInquiryStatus = async (id, status) => {
    try {
      await api.patch(`/inquiries/${id}`, { status }, adminHeaders());
      toast.success("Updated");
      loadAll();
    } catch {
      toast.error("Could not update");
    }
  };

  const deleteInquiry = async (id) => {
    if (!window.confirm("Delete this inquiry?")) return;
    try {
      await api.delete(`/inquiries/${id}`, adminHeaders());
      toast.success("Deleted");
      loadAll();
    } catch {
      toast.error("Could not delete");
    }
  };

  return (
    <div data-testid="admin-dashboard-layout" className="min-h-screen bg-[color:var(--brand-surface-alt)]">
      <header className="bg-[color:var(--brand-surface)] border-b border-[color:var(--brand-border)]">
        <div className="container-narrow py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img
              src={LOGO_URL}
              alt="Journey To Andaman"
              className="h-10 w-auto object-contain"
            />
            <div>
              <div className="font-heading text-lg leading-none">Journey To Andaman</div>
              <span className="overline text-[10px]">Admin</span>
            </div>
          </Link>
          <Button variant="outline" onClick={logout} data-testid="admin-logout" className="gap-2">
            <LogOut className="w-4 h-4" /> Sign out
          </Button>
        </div>
      </header>

      <main className="container-narrow py-10">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8" data-testid="admin-stats">
          {[
            { k: "total_packages", label: "Total packages" },
            { k: "active_packages", label: "Active" },
            { k: "total_inquiries", label: "Total inquiries" },
            { k: "new_inquiries", label: "New inquiries" },
          ].map((s) => (
            <div key={s.k} className="bg-[color:var(--brand-surface)] border border-[color:var(--brand-border)] p-5">
              <div className="overline">{s.label}</div>
              <div className="font-heading text-4xl mt-2">{stats?.[s.k] ?? "—"}</div>
            </div>
          ))}
        </div>

        <Tabs defaultValue="packages">
          <TabsList>
            <TabsTrigger value="packages" data-testid="tab-packages">Packages</TabsTrigger>
            <TabsTrigger value="inquiries" data-testid="tab-inquiries">Inquiries</TabsTrigger>
          </TabsList>

          <TabsContent value="packages" className="mt-6">
            <div className="flex justify-end mb-4">
              <Dialog open={creating} onOpenChange={setCreating}>
                <DialogTrigger asChild>
                  <Button className="btn-primary gap-2" data-testid="new-package-btn">
                    <Plus className="w-4 h-4" /> New package
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="font-heading text-2xl">New package</DialogTitle>
                  </DialogHeader>
                  <PackageForm
                    initial={EMPTY_PKG}
                    onSave={createPkg}
                    onCancel={() => setCreating(false)}
                  />
                </DialogContent>
              </Dialog>
            </div>

            <div className="bg-[color:var(--brand-surface)] border border-[color:var(--brand-border)]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Duration</TableHead>
                    <TableHead>Price</TableHead>
                    <TableHead>Featured</TableHead>
                    <TableHead>Active</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {packages.map((p) => (
                    <TableRow key={p.id} data-testid={`admin-pkg-row-${p.id}`}>
                      <TableCell className="font-medium">{p.title}</TableCell>
                      <TableCell>{p.category}</TableCell>
                      <TableCell>{p.duration_days}D/{p.duration_nights}N</TableCell>
                      <TableCell>{formatINR(p.price_inr)}</TableCell>
                      <TableCell>{p.featured ? "Yes" : "—"}</TableCell>
                      <TableCell>{p.active ? "Yes" : "—"}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setEditing(p)}
                            data-testid={`edit-pkg-${p.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => deletePkg(p.id)}
                            data-testid={`delete-pkg-${p.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {packages.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-[color:var(--brand-text-soft)]">
                        No packages yet.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>

            <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
              <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-heading text-2xl">Edit package</DialogTitle>
                </DialogHeader>
                {editing && (
                  <PackageForm
                    initial={editing}
                    onSave={updatePkg}
                    onCancel={() => setEditing(null)}
                  />
                )}
                <DialogFooter />
              </DialogContent>
            </Dialog>
          </TabsContent>

          <TabsContent value="inquiries" className="mt-6">
            <div className="bg-[color:var(--brand-surface)] border border-[color:var(--brand-border)]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Customer</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Package</TableHead>
                    <TableHead>Travelers</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {inquiries.map((i) => (
                    <TableRow key={i.id} data-testid={`inquiry-row-${i.id}`}>
                      <TableCell>
                        <div className="font-medium">{i.customer_name}</div>
                        <div className="text-xs text-[color:var(--brand-text-soft)]">
                          {new Date(i.created_at).toLocaleString()}
                        </div>
                        {i.message && (
                          <div className="text-xs mt-1 text-[color:var(--brand-text-soft)] max-w-xs truncate" title={i.message}>
                            "{i.message}"
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-xs">
                        <div>{i.email}</div>
                        <div>{i.phone}</div>
                      </TableCell>
                      <TableCell className="text-sm">{i.package_title || "—"}</TableCell>
                      <TableCell>{i.travelers}</TableCell>
                      <TableCell>{i.travel_date || "—"}</TableCell>
                      <TableCell>
                        <Select
                          value={i.status}
                          onValueChange={(v) => updateInquiryStatus(i.id, v)}
                        >
                          <SelectTrigger className="w-36" data-testid={`inquiry-status-${i.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {STATUSES.map((s) => (
                              <SelectItem key={s} value={s}>{s}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => deleteInquiry(i.id)}
                          data-testid={`delete-inquiry-${i.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {inquiries.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-[color:var(--brand-text-soft)]">
                        No inquiries yet.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
